import { v4 as uuidv4 } from "uuid"

// Endpoints
import { AuthEndpoints } from "./endpoints/AuthEndpoints"
import { BookingEndpoints } from "./endpoints/BookingEndpoints"
import { CalendarEndpoints } from "./endpoints/CalendarEndpoints"
import { ClientEndpoints } from "./endpoints/ClientEndpoints"
import { CoachEndpoints } from "./endpoints/CoachEndpoints"
import { EventEndpoints } from "./endpoints/EventEndpoints"
import { InvoiceEndpoints } from "./endpoints/InvoiceEndpoints"
import { JobEndpoints } from "./endpoints/JobEndpoints"
import { MeetingEndpoints } from "./endpoints/MeetingEndpoints"
import { QuizEndpoints } from "./endpoints/QuizEndpoints"
import { ReportEndpoints } from "./endpoints/ReportEndpoints"
import { StripeEndpoints } from "./endpoints/StripeEndpoints"
import { UserEndpoints } from "./endpoints/UserEndpoints"

type HttpHeader = Record<string, string>
type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH"

export interface RetryConfig {
  maxRetries?: number
  baseDelay?: number
  idempotencyKey?: string
}

/**
 * Error class for API errors.
 *
 * @example
 * try {
 *      await api.user.getMe()
 * } catch (error) {
 *      if (error instanceof ApiError) {
 *          console.error(`API error: ${error.status} - ${error.message}`)
 *      } else {
 *          console.error("Unknown error", error)
 *      }
 * }
 */
export class ApiError extends Error {
  status: number
  message: string

  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.message = message
    Object.setPrototypeOf(this, ApiError.prototype)
  }
}

/**
 * Main API client class that provides access to all endpoint groups.
 *
 * @example
 * // Create an instance of the API client
 * const api = new ApiClient("https://api.example.com")
 *
 * // Use the API client to call an endpoint
 * try {
 *    const user = await api.user.getMe()
 *    console.log(user
 * } catch (error) {
 *    if (error instanceof ApiError) {
 *       console.error(`API error: ${error.status} - ${error.message}`)
 *    } else {
 *      console.error("Unknown error", error)
 *    }
 * }
 */
export default class ApiClient {
  public readonly baseUrl: string

  // Endpoint groups
  public readonly auth: AuthEndpoints
  public readonly booking: BookingEndpoints
  public readonly calendar: CalendarEndpoints
  public readonly client: ClientEndpoints
  public readonly coach: CoachEndpoints
  public readonly event: EventEndpoints
  public readonly invoice: InvoiceEndpoints
  public readonly job: JobEndpoints
  public readonly meeting: MeetingEndpoints
  public readonly quiz: QuizEndpoints
  public readonly report: ReportEndpoints
  public readonly stripe: StripeEndpoints
  public readonly user: UserEndpoints

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
    this.auth = new AuthEndpoints(this)
    this.booking = new BookingEndpoints(this)
    this.calendar = new CalendarEndpoints(this)
    this.client = new ClientEndpoints(this)
    this.coach = new CoachEndpoints(this)
    this.event = new EventEndpoints(this)
    this.invoice = new InvoiceEndpoints(this)
    this.job = new JobEndpoints(this)
    this.meeting = new MeetingEndpoints(this)
    this.quiz = new QuizEndpoints(this)
    this.report = new ReportEndpoints(this)
    this.stripe = new StripeEndpoints(this)
    this.user = new UserEndpoints(this)
  }

  /**
   * Performs a GET request.
   *
   * @example
   * // Retrieve the currently authenticated user
   * const user = await api.get<UserGetMeOut>("/user", "/me")
   */
  async get<T>(basePath: string, path: string, query: any = {}, headers: HttpHeader = {}): Promise<T> {
    const url = ApiClient.generateUrlWithParams(basePath + path, query)
    return this.performRequest(url, "GET", null, headers)
  }

  /**
   * Performs a POST request.
   *
   * @example
   * // Create a new user
   * await api.post<void>("/user", "/", { name: "John Doe" })
   *
   * // Create a payment with retry logic
   * await api.post<PaymentResponse>("/payments", "/", { amount: 100 }, {}, { maxRetries: 5, baseDelay: 2000 })
   */
  async post<T>(
    basePath: string,
    path: string,
    payload: any,
    headers: HttpHeader = {},
    retryConfig?: RetryConfig
  ): Promise<T> {
    if (retryConfig) {
      return this.performRequestWithRetryAndBackoff(basePath + path, "POST", payload, headers, retryConfig)
    }
    return this.performRequest(basePath + path, "POST", payload, headers)
  }

  /**
   * Performs a PATCH request.
   *
   * @example
   * // Update part of a coach's record
   * await api.patch<void>("/coach", `/${coachId}/hourly-rate`, { hourlyRate: 45 })
   *
   * // Update with retry logic
   * await api.patch<void>("/coach", `/${coachId}/hourly-rate`, { hourlyRate: 45 }, {}, { maxRetries: 3 })
   */
  async patch<T>(
    basePath: string,
    path: string,
    payload: any,
    headers: HttpHeader = {},
    retryConfig?: RetryConfig
  ): Promise<T> {
    if (retryConfig) {
      return this.performRequestWithRetryAndBackoff(basePath + path, "PATCH", payload, headers, retryConfig)
    }
    return this.performRequest(basePath + path, "PATCH", payload, headers)
  }

  /**
   * Performs a PUT request.
   *
   * @example
   * // Update an existing meeting note
   * await api.put<void>("/meeting", `/${meetingNoteId}`, { ...payload })
   *
   * // Update with retry logic
   * await api.put<void>("/meeting", `/${meetingNoteId}`, { ...payload }, {}, { maxRetries: 5, baseDelay: 2000 })
   */
  async put<T>(
    basePath: string,
    path: string,
    payload: any,
    headers: HttpHeader = {},
    retryConfig?: RetryConfig
  ): Promise<T> {
    if (retryConfig) {
      return this.performRequestWithRetryAndBackoff(basePath + path, "PUT", payload, headers, retryConfig)
    }
    return this.performRequest(basePath + path, "PUT", payload, headers)
  }

  /**
   * Performs a DELETE request.
   *
   * @example
   * // Delete a meeting note
   * await api.delete<void>("/meeting", `/${meetingNoteId}`)
   */
  async delete<T>(basePath: string, path: string, headers: HttpHeader = {}): Promise<T> {
    return this.performRequest(basePath + path, "DELETE", null, headers)
  }

  /**
   * STATIC UTILITY METHODS
   */

  /**
   * Creates common retry configuration presets.
   *
   * @example
   * // Use a fast retry configuration
   * await api.post("/endpoint", "/", data, {}, ApiClient.retryConfig.fast())
   *
   * // Use a conservative retry configuration
   * await api.patch("/endpoint", "/", data, {}, ApiClient.retryConfig.conservative())
   *
   * // Create a custom configuration
   * await api.put("/endpoint", "/", data, {}, ApiClient.retryConfig.custom(5, 500))
   */
  static retryConfig = {
    fast: (): RetryConfig => ({ maxRetries: 2, baseDelay: 500 }),
    default: (): RetryConfig => ({ maxRetries: 3, baseDelay: 1000 }),
    conservative: (): RetryConfig => ({ maxRetries: 5, baseDelay: 2000 }),
    custom: (maxRetries: number, baseDelay: number, idempotencyKey?: string): RetryConfig => ({
      maxRetries,
      baseDelay,
      idempotencyKey
    })
  }

  /**
   * Generates a unique idempotency key.
   *
   * @example
   * const key = ApiClient.generateIdempotencyKey()
   * await api.post("/endpoint", "/", data, {}, { idempotencyKey: key })
   */
  static generateIdempotencyKey(): string {
    return uuidv4()
  }

  /**
   * Takes a base URL and generates a URL with the specified query parameters.
   */
  static generateUrlWithParams(
    baseUrl: string,
    params: Record<string, string | number | boolean | Date | any[]>
  ): string {
    const queryParams = Object.entries(params)
      .filter(([, value]) => value !== null)
      .map(([key, value]) =>
        Array.isArray(value)
          ? value.map((val) => `${encodeURIComponent(key)}=${encodeURIComponent(val as string)}`).join("&")
          : value instanceof Date
            ? `${encodeURIComponent(key)}=${encodeURIComponent(value.toISOString())}`
            : `${encodeURIComponent(key)}=${encodeURIComponent(value as string)}`
      )
      .join("&")

    return baseUrl + (baseUrl.includes("?") ? "&" : "?") + queryParams
  }

  /**
   * PRIVATE METHODS
   */

  /**
   * Generic request performer.
   */
  private async performRequest<T>(
    path: string,
    method: HttpMethod,
    payload: any = null,
    headers: HttpHeader = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`

    headers["Accept"] = "application/json"
    headers["Content-Type"] = "application/json"

    const body = payload ? JSON.stringify(payload) : null

    const response = await fetch(url, { method, headers, body, credentials: "include" })

    return await ApiClient.verifyResponse(response)
  }

  /**
   * Performs a request with exponential backoff retry logic.
   * Used for endpoints that may need to be polled or retried.
   * Includes an idempotency key to ensure safe retries.
   *
   * @example
   * // Advanced usage for custom endpoints
   * await api.performRequestWithRetryAndBackoff("/custom", "POST", data, {}, { maxRetries: 5 })
   */
  async performRequestWithRetryAndBackoff<T>(
    path: string,
    method: HttpMethod,
    payload: any = null,
    headers: HttpHeader = {},
    retryConfig: RetryConfig = {}
  ): Promise<T> {
    const { maxRetries = 3, baseDelay = 1000, idempotencyKey } = retryConfig
    let lastError: Error | null = null

    // Generate an idempotency key if not provided (for non-GET requests)
    const requestHeaders = { ...headers }
    if (method !== "GET" && method !== "DELETE") {
      requestHeaders["Idempotency-Key"] = idempotencyKey || uuidv4()
    }

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await this.performRequest<T>(path, method, payload, requestHeaders)
      } catch (error) {
        lastError = error as Error

        // Don't retry for client errors (4xx) except for 429 (rate limiting)
        if (error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 429) {
          throw error
        }

        // If this is the last attempt, throw the error
        if (attempt === maxRetries) {
          throw error
        }

        // Calculate exponential backoff delay
        const delay = baseDelay * Math.pow(2, attempt)
        const jitter = Math.random() * 0.1 * delay // Add 10% jitter

        await new Promise((resolve) => setTimeout(resolve, delay + jitter))
      }
    }

    throw lastError
  }

  /**
   * Checks response and throws an error if it's not OK.
   */
  private static async verifyResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      throw new ApiError(response.status, (await response.json())?.message)
    }
    return await response.json()
  }
}
